import React from "react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { ToolIcon } from "../../../components/classic/panels";
import { fmtDur, fmtClockTz } from "../bits";
import { Sk } from "./storyBits";

/* AI tools we can name from /day — app name, window title, or URL.
   Same family list the company brain already uses. No extra API. */
const AI_TOOLS = [
  { key: "cursor", label: "Cursor", app: /\bcursor\b/i, hay: /\bcursor\b/i },
  { key: "chatgpt", label: "ChatGPT", app: /\bchatgpt\b|\bopenai\b/i, hay: /chatgpt|openai\.com/i },
  { key: "claude", label: "Claude", app: /\bclaude\b/i, hay: /\bclaude(\.ai)?\b/i },
  { key: "gemini", label: "Gemini", app: /\bgemini\b|\bbard\b/i, hay: /\bgemini\b|bard\.google|gemini\.google/i },
  { key: "copilot", label: "Copilot", app: /\bcopilot\b/i, hay: /copilot|github copilot/i },
  { key: "perplexity", label: "Perplexity", app: /\bperplexity\b/i, hay: /perplexity/i },
  { key: "windsurf", label: "Windsurf", app: /\bwindsurf\b|\bcodeium\b/i, hay: /windsurf|codeium/i },
  { key: "grok", label: "Grok", app: /\bgrok\b/i, hay: /\bgrok\b|x\.ai/i },
];

function classify(b) {
  const app = String(b.app || "");
  const hay = `${b.title || ""} ${b.url || ""} ${(b.artifacts || []).join(" ")}`;
  for (const t of AI_TOOLS) {
    if (t.app.test(app) || t.hay.test(hay)) return t;
  }
  return null;
}

function uniq(list, limit = 6) {
  const out = [];
  const seen = new Set();
  for (const raw of list || []) {
    const s = String(typeof raw === "string" ? raw : raw?.name || "").trim();
    if (!s || s.length < 2) continue;
    const k = s.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
    if (out.length >= limit) break;
  }
  return out;
}

export function aiAdoptionMeta(blocks, apps, totals) {
  const m = buildModel(blocks, apps, totals);
  if (!m.tools.length) return "None today";
  return `${fmtDur(m.aiMin)} · ${m.share}%`;
}

function buildModel(blocks, apps, totals) {
  const working = (blocks || []).filter((b) => b.cat !== "idle" && b.minutes > 0);
  const hits = [];
  for (const b of working) {
    const tool = classify(b);
    if (tool) hits.push({ ...b, tool });
  }
  const dayMin = Math.max(1, totals?.trackedMin || working.reduce((a, b) => a + (b.minutes || 0), 0));
  const by = {};
  for (const b of hits) {
    const k = b.tool.key;
    if (!by[k]) by[k] = { ...b.tool, minutes: 0, sittings: 0, artifacts: [], firstMs: b.startMs, lastMs: b.endMs, appName: b.app };
    by[k].minutes += b.minutes || 0;
    by[k].sittings += 1;
    by[k].artifacts.push(...(b.artifacts || []), b.title);
    by[k].firstMs = Math.min(by[k].firstMs, b.startMs || by[k].firstMs);
    by[k].lastMs = Math.max(by[k].lastMs, b.endMs || by[k].lastMs);
    if (b.tool.app.test(String(b.app || ""))) by[k].appName = b.app;
  }
  // Apps list can catch a named AI tool even if blocks missed the title.
  for (const a of apps || []) {
    const tool = AI_TOOLS.find((t) => t.app.test(a.name || ""));
    if (!tool || by[tool.key]) continue;
    by[tool.key] = {
      ...tool, minutes: a.minutes || 0, sittings: 1, artifacts: [],
      firstMs: null, lastMs: null, appName: a.name,
    };
  }
  const tools = Object.values(by).filter((x) => x.minutes > 0).sort((a, b) => b.minutes - a.minutes)
    .map((x) => ({ ...x, artifacts: uniq(x.artifacts, 5) }));
  const aiMin = tools.reduce((a, x) => a + x.minutes, 0);
  return {
    tools,
    aiMin,
    share: Math.round((aiMin / dayMin) * 100),
    dayMin,
    sittings: hits.length,
  };
}

export function AiAdoptionSk() {
  return (
    <div aria-hidden="true" style={{ padding: "16px 20px 20px" }}>
      <Sk w="68%" h={16} style={{ marginBottom: 14 }} />
      <Sk h={10} r={99} style={{ marginBottom: 16 }} />
      {[0, 1].map((i) => (
        <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", marginTop: i ? 12 : 0 }}>
          <Sk w={32} h={32} r={8} style={{ flex: "none" }} />
          <Sk w={90} h={12} style={{ flex: "none" }} />
          <Sk h={8} r={99} style={{ flex: 1 }} />
          <Sk w={48} h={14} style={{ flex: "none" }} />
        </div>
      ))}
    </div>
  );
}

export function AiAdoption({ blocks, apps, totals, offsetMin, onOpenApp, loading }) {
  const t = useDashTheme();
  const model = React.useMemo(
    () => buildModel(blocks, apps, totals),
    [blocks, apps, totals],
  );

  if (loading) return <AiAdoptionSk />;

  if (!model.tools.length) {
    return (
      <div style={{ padding: "16px 20px 20px", fontSize: 13.5, color: t.c.textSecondary, lineHeight: 1.5 }}>
        No AI tools on this day — Cursor, ChatGPT, Claude, Gemini, Copilot and the rest did not show up in the tracked hours.
      </div>
    );
  }

  const names = model.tools.map((x) => x.label);
  const juice = model.tools.length === 1
    ? `${fmtDur(model.aiMin)} in ${names[0]} — ${model.share}% of the day.`
    : `${fmtDur(model.aiMin)} across ${names.join(", ")} — ${model.share}% of the day.`;

  return (
    <div style={{ padding: "16px 20px 20px" }}>
      <p style={{
        margin: "0 0 12px", fontSize: 15.5, fontWeight: 550, letterSpacing: "-0.02em",
        lineHeight: 1.4, color: t.c.text, maxWidth: 640,
      }}>{juice}</p>

      <div style={{
        display: "flex", height: 10, borderRadius: 99, overflow: "hidden",
        background: t.c.surfaceMuted, marginBottom: 8,
      }}>
        <i style={{
          width: `${Math.max(4, model.share)}%`, display: "block",
          background: t.c.ai,
        }} />
      </div>
      <div style={{ fontSize: 12, color: t.c.textTertiary, marginBottom: 14 }}>
        {model.share}% of tracked time · {model.sittings || model.tools.length} sitting{ (model.sittings || model.tools.length) === 1 ? "" : "s"}
      </div>

      <div style={{
        border: `1px solid ${t.c.border}`, borderRadius: t.r.md, overflow: "hidden",
        background: t.isDark ? t.c.surfaceMuted : "#F7F8FB",
      }}>
        {model.tools.map((x, i) => {
          const pct = Math.round((x.minutes / Math.max(1, model.aiMin)) * 100);
          const when = x.firstMs && x.lastMs
            ? `${fmtClockTz(x.firstMs, offsetMin)} – ${fmtClockTz(x.lastMs, offsetMin)}`
            : "";
          return (
            <button
              key={x.key}
              type="button"
              className="st-reset st-brief-note st-focusable"
              onClick={() => onOpenApp?.(x.appName || x.label)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "13px 14px", background: "transparent", border: 0,
                borderTop: i ? `1px solid ${t.c.border}` : "none", cursor: "pointer",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ToolIcon name={x.appName || x.label} size={32} />
                <span style={{ flex: "0 0 120px", minWidth: 88 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 650 }}>{x.label}</span>
                  <span style={{ display: "block", fontSize: 12, color: t.c.textTertiary, marginTop: 2 }}>
                    {x.sittings} sitting{x.sittings === 1 ? "" : "s"}{when ? ` · ${when}` : ""}
                  </span>
                </span>
                <span style={{
                  flex: 1, height: 8, borderRadius: 99, overflow: "hidden",
                  background: t.isDark ? t.c.surface : "#fff",
                }}>
                  <i style={{
                    display: "block", height: "100%", borderRadius: 99, background: t.c.ai,
                    width: `${Math.max(6, (x.minutes / model.tools[0].minutes) * 100)}%`,
                  }} />
                </span>
                <span style={{
                  flex: "none", width: 48, textAlign: "right", fontSize: 16, fontWeight: 720,
                  letterSpacing: "-0.03em", color: t.c.ai, fontVariantNumeric: "tabular-nums",
                }}>{pct}%</span>
                <span style={{
                  flex: "none", width: 58, textAlign: "right", fontSize: 12.5, fontWeight: 650,
                  color: t.c.textTertiary, fontVariantNumeric: "tabular-nums",
                }}>{fmtDur(x.minutes)}</span>
              </span>
              {x.artifacts.length > 0 && (
                <span style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "9px 0 0 44px" }}>
                  {x.artifacts.map((a) => (
                    <span key={a} style={{
                      fontSize: 11, fontWeight: 500, color: t.c.textSecondary,
                      background: t.isDark ? t.c.surface : "#fff",
                      border: `1px solid ${t.c.border}`,
                      borderRadius: t.r.pill, padding: "3px 8px",
                    }}>{a}</span>
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
